import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationPreference } from '../../interfaces/notification.interface';
import { useToast } from '../../context/ToastContext';

const NotificationPreferencesPage: React.FC = () => {
    const toast = useToast();
    const [pref, setPref] = useState<NotificationPreference>({ emailDigest: 'DAILY', smsEnabled: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setLoading(false); return; }
        NotificationService.getPreferences(institutionId)
            .then(setPref)
            .catch(err => toast.showApiError(err, 'Failed to load notification preferences'))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setSaving(true);
            setPref(await NotificationService.savePreferences(institutionId, pref));
            toast.showSuccess('Preferences saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: 640 }} data-help-page="notifications.prefs.page">
            <h2>Notification preferences</h2>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                Reminders always appear in your in-app inbox. Choose whether you also get a daily e-mail digest, and
                SMS for overdue items (SMS is sent only once your administrator connects an SMS provider).
            </Typography>
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                        data-help="notifications.prefs.field.emailDigest"
                        control={<Switch checked={pref.emailDigest === 'DAILY'}
                            onChange={e => setPref({ ...pref, emailDigest: e.target.checked ? 'DAILY' : 'OFF' })} />}
                        label="Daily e-mail digest"
                    />
                    <FormControlLabel
                        data-help="notifications.prefs.field.sms"
                        control={<Switch checked={pref.smsEnabled} onChange={e => setPref({ ...pref, smsEnabled: e.target.checked })} />}
                        label="SMS for overdue items"
                    />
                    {pref.smsEnabled && (
                        <Alert severity="info">SMS uses the phone number on your profile.</Alert>
                    )}
                    <Box><Button variant="contained" onClick={save} disabled={saving} data-help="notifications.prefs.save">Save</Button></Box>
                </Box>
            )}
        </div>
    );
};

export default NotificationPreferencesPage;
